import { useState } from "react";
import { api } from "../../lib/api";
import { UploadCloud, X, Loader2, FileText } from "lucide-react";
import { fileUrl } from "../../lib/api";

export const inputCls = "w-full border border-[#E5DECF] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E6C687] bg-white";
export const labelCls = "text-xs font-semibold uppercase tracking-wider text-[#5A6270] mb-1 block";

export function Btn({ children, variant = "gold", ...props }) {
  const base = "rounded-lg px-4 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-2 disabled:opacity-60";
  const styles = {
    gold: "btn-gold",
    outline: "border border-[#E5DECF] text-[#5A6270] hover:border-[#E6C687] bg-white",
    danger: "bg-[#C53030] text-white hover:bg-[#a52626]",
    maroon: "bg-[#0B2E63] text-white hover:bg-[#16457F]",
  };
  return <button className={`${base} ${styles[variant]}`} {...props}>{children}</button>;
}

export function Field({ label, children }) {
  return <div><label className={labelCls}>{label}</label>{children}</div>;
}

export function Panel({ title, actions, children }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5DECF] overflow-hidden">
      <div className="px-5 py-4 border-b border-[#F0E9DA] flex items-center justify-between">
        <h2 className="font-display text-lg text-[#0B2E63] font-semibold">{title}</h2>
        {actions}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

/** Uploads files, returns array of storage paths via onChange */
export function ImageUploader({ paths = [], onChange, max = 10 }) {
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    const arr = Array.from(files).slice(0, max - paths.length);
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      onChange([...paths, ...uploaded]);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-3">
        {paths.map((p, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-[#E5DECF] group">
            <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(paths.filter((_, idx) => idx !== i))}
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100" data-testid={`remove-image-${i}`}>
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
      {paths.length < max && (
        <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-[#E6C687] rounded-lg px-4 py-3 text-sm text-[#123B7A] hover:bg-[#EAF1FB] w-fit" data-testid="image-upload-input">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
          {busy ? "Uploading…" : `Upload images (${paths.length}/${max})`}
          <input type="file" accept="image/*" multiple hidden disabled={busy} onChange={(e) => e.target.files.length && upload(e.target.files)} />
        </label>
      )}
    </div>
  );
}

export function SingleImageUploader({ path, onChange, label = "Upload image", accept = "image/*" }) {
  const [busy, setBusy] = useState(false);
  const isPdf = path && path.toLowerCase().endsWith(".pdf");
  const upload = async (f) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-4">
      {path && (isPdf ? (
        <a href={fileUrl(path)} target="_blank" rel="noreferrer" className="w-20 h-20 rounded-lg border border-[#E5DECF] bg-[#EAF1FB] flex flex-col items-center justify-center text-[#123B7A] text-xs gap-1 font-semibold">
          <FileText size={22} /> PDF
        </a>
      ) : (
        <img src={fileUrl(path)} alt="" className="w-20 h-20 rounded-lg object-cover border border-[#E5DECF]" />
      ))}
      <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-[#E6C687] rounded-lg px-4 py-3 text-sm text-[#123B7A] hover:bg-[#EAF1FB]">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />} {busy ? "Uploading…" : label}
        <input type="file" accept={accept} hidden disabled={busy} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      </label>
    </div>
  );
}
